import { lang } from '@umu/ukit-lang';
import { useInfiniteScroll, useMemoizedFn } from 'ahooks';
import { BasicTarget } from 'ahooks/lib/utils/domTarget';
import {
    fetchPracticeInviteScore,
    fetchPracticeInviteUser,
} from 'businessCommon/src/components/inviteEvaluationList/request';
import {
    EInviteScoreStatus,
    EPracticeInviteStatus,
    IPracticeInviteUserList,
} from 'businessCommon/src/components/inviteEvaluationList/types';
import { PRACTICE_TYPE } from 'businessCommon/src/components/resourceSettingModal/interface';
import { Toast } from 'common/src/services';
import { useState } from 'react';

interface UseInviteListProps {
    practiceId: string;
    seId: string;
    objId: string;
    channelId: string;
    objType?: PRACTICE_TYPE;
    scrollTarget?: BasicTarget<Element | Document>;
}

const useInviteList = ({
    practiceId,
    seId,
    objId,
    channelId,
    objType = PRACTICE_TYPE.HOMEWORK_VIDEO,
    scrollTarget,
}: UseInviteListProps) => {
    const [inviteUserNum, setInviteUserNum] = useState(0);

    const getUserList = async (params: { current: number; pageSize: number }) => {
        const res = await fetchPracticeInviteUser({
            obj_id: objId,
            obj_type: objType,
            ...(seId ? { se_id: seId } : {}),
            practice_id: practiceId,
            page: params.current,
            size: params.pageSize,
            channel_id: channelId,
        });
        setInviteUserNum(+res?.invite_number || 0);
        return {
            pageInfo: res?.page_info || {},
            current: res?.page_info?.current_page || 1,
            list: res?.list || [],
        };
    };

    const { data, loading, loadingMore, noMore, mutate } = useInfiniteScroll(
        (d) => getUserList({ current: d ? +d.pageInfo?.current_page + 1 : 1, pageSize: d?.pageInfo?.page_size || 10 }),
        {
            isNoMore: (d) => d?.pageInfo?.current_page >= d?.pageInfo?.total_page_num,
            target: scrollTarget,
        },
    );

    const updateInviteStatus = useMemoizedFn((record: IPracticeInviteUserList, status: EPracticeInviteStatus) => {
        if (![EInviteScoreStatus.NOT_INVITED, EInviteScoreStatus.INVITED].includes(record.invite_status)) {
            return;
        }

        const isAddInvite =
            record.invite_status === EInviteScoreStatus.NOT_INVITED && status === EPracticeInviteStatus.INVITE;
        const isCancelInvite =
            record.invite_status === EInviteScoreStatus.INVITED && status === EPracticeInviteStatus.CANCEL_INVITE;

        setInviteUserNum((num) => (isAddInvite ? num + 1 : isCancelInvite ? num - 1 : num));

        const list =
            data?.list.map((item) =>
                item.user_info.umu_id === record.user_info.umu_id
                    ? {
                          ...item,
                          invite_status:
                              status === EPracticeInviteStatus.INVITE
                                  ? EInviteScoreStatus.INVITED
                                  : EInviteScoreStatus.NOT_INVITED,
                      }
                    : item,
            ) || [];

        mutate({ ...data, list });
    });

    const handleToInviteComment = useMemoizedFn((record: IPracticeInviteUserList, status: EPracticeInviteStatus) => {
        fetchPracticeInviteScore({
            obj_id: objId,
            obj_type: objType,
            umu_ids: record?.user_info?.umu_id || '',
            invite_status: status,
            practice_id: practiceId,
            channel_id: channelId,
        })
            .then(() => {
                Toast.show(
                    status === EPracticeInviteStatus.INVITE
                        ? record.invite_status === EInviteScoreStatus.INVITED
                            ? lang('已再次发送邀请通知')
                            : lang('邀请点评成功')
                        : lang('撤回点评邀请成功'),
                );
                updateInviteStatus(record, status);
            })
            .catch(console.error);
    });

    return { data, loading, loadingMore, noMore, inviteUserNum, handleToInviteComment };
};

export { useInviteList };
